const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getToken = () => localStorage.getItem('findash_token');

const downloadFile = async (path, filenameFallback) => {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
  });
  if (!res.ok) throw new Error('Download failed');

  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="(.+)"/);
  const filename = match ? match[1] : filenameFallback;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const request = async (path, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem('findash_token');
    window.location.reload();
    return;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  return res.json();
};

export const api = {
  // Auth
  login: (email, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (data) =>
    request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getStates: () => request('/api/auth/states'),

  saveTaxSettings: (state, filingStatus) =>
    request('/api/auth/tax-settings', {
      method: 'PUT',
      body: JSON.stringify({ state, filingStatus }),
    }),

  getIncomeTracker: () => request('/api/income/tracker'),

  autoSyncCheck: () => request('/api/plaid/auto-sync-check', { method: 'POST' }),

  getCategoryBudgets: () => request('/api/category-budgets'),

  setCategoryBudget: (category, amount) =>
    request(`/api/category-budgets/${encodeURIComponent(category)}`, {
      method: 'PUT',
      body: JSON.stringify({ amount }),
    }),

  resetCategoryBudget: (category) =>
    request(`/api/category-budgets/${encodeURIComponent(category)}`, { method: 'DELETE' }),

  getSavingsGoals: () => request('/api/savings-goals'),

  addSavingsGoal: (data) =>
    request('/api/savings-goals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSavingsGoal: (id, data) =>
    request(`/api/savings-goals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  contributeSavingsGoal: (id, delta) =>
    request(`/api/savings-goals/${id}/contribute`, {
      method: 'PUT',
      body: JSON.stringify({ delta }),
    }),

  deleteSavingsGoal: (id) =>
    request(`/api/savings-goals/${id}`, { method: 'DELETE' }),

  getCategoriesList: () => request('/api/transactions/categories-list'),

  exportCsv: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return downloadFile(`/api/transactions/export/csv${qs ? `?${qs}` : ''}`, 'transactions.csv');
  },

  exportPdfReport: () => downloadFile('/api/reports/summary-pdf', 'report.pdf'),

  getDebts: () => request('/api/debts'),

  addDebt: (data) =>
    request('/api/debts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateDebt: (id, data) =>
    request(`/api/debts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  markDebtPaidOff: (id) =>
    request(`/api/debts/${id}/paid-off`, { method: 'PUT' }),

  deleteDebt: (id) =>
    request(`/api/debts/${id}`, { method: 'DELETE' }),

  getChecklist: () => request('/api/checklist'),
  getStuckPendingTransactions: () => request('/api/transactions/stuck-pending'),
  markTransactionPosted: (id) => request(`/api/transactions/${id}/mark-posted`, { method: 'POST' }),
  dismissPendingTransaction: (id) => request(`/api/transactions/${id}/dismiss-pending`, { method: 'DELETE' }),
  getRecentTransfers: () => request('/api/transactions/recent-transfers'),
  markNotTransfer: (merchantName) =>
    request('/api/income/transfers/not-transfer', {
      method: 'POST',
      body: JSON.stringify({ merchantName }),
    }),
  confirmTransfer: (id) => request(`/api/transactions/${id}/confirm-transfer`, { method: 'POST' }),

  updateDebtExtraPayment: (amount) =>
    request('/api/debts/settings/extra-payment', {
      method: 'PUT',
      body: JSON.stringify({ amount }),
    }),

  recategorizeTransaction: (id, category, scope = 'transaction') =>
    request(`/api/transactions/${id}/category`, {
      method: 'PUT',
      body: JSON.stringify({ category, scope }),
    }),

  resetTransactionCategory: (id, scope = 'transaction') =>
    request(`/api/transactions/${id}/category?scope=${scope}`, { method: 'DELETE' }),

  getTransactionSplits: (id) => request(`/api/transactions/${id}/splits`),

  saveTransactionSplits: (id, splits) =>
    request(`/api/transactions/${id}/splits`, {
      method: 'PUT',
      body: JSON.stringify({ splits }),
    }),

  deleteTransactionSplits: (id) =>
    request(`/api/transactions/${id}/splits`, { method: 'DELETE' }),

  submitFeedback: (message, pageContext) =>
    request('/api/feedback', {
      method: 'POST',
      body: JSON.stringify({ message, pageContext }),
    }),

  getFeedback: () => request('/api/feedback'),

  updateFeedbackStatus: (id, status) =>
    request(`/api/feedback/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  estimateBudget: (data) =>
    request('/api/auth/estimate-budget', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  me: () => request('/api/auth/me'),

  verify2FALogin: (pendingToken, code) =>
    request('/api/auth/2fa/login-verify', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    }),

  setup2FA: () => request('/api/auth/2fa/setup', { method: 'POST' }),

  verifySetup2FA: (code) =>
    request('/api/auth/2fa/verify-setup', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  disable2FA: (password) =>
    request('/api/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  dismiss2FAPrompt: () =>
    request('/api/auth/dismiss-2fa-prompt', { method: 'PUT' }),

  updateBudget: (annualBudget) =>
    request('/api/auth/budget', {
      method: 'PUT',
      body: JSON.stringify({ annualBudget }),
    }),

  updateBudgetMode: (mode) =>
    request('/api/auth/budget-mode', {
      method: 'PUT',
      body: JSON.stringify({ mode }),
    }),

  updateNormalizeMissingMonths: (enabled) =>
    request('/api/auth/normalize-missing-months', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  updateAnnualBudget: (annualBudget, source = 'manual') =>
    request('/api/auth/budget', {
      method: 'PUT',
      body: JSON.stringify({ annualBudget, source }),
    }),

  getBudgetRecommendation: () => request('/api/auth/budget-recommendation'),

  updateBudgetRecommendationSettings: (incomeIncluded, investmentIncluded) =>
    request('/api/auth/budget-recommendation-settings', {
      method: 'PUT',
      body: JSON.stringify({ incomeIncluded, investmentIncluded }),
    }),

  updateSessionTimeout: (hours) =>
    request('/api/auth/session-timeout', {
      method: 'PUT',
      body: JSON.stringify({ hours }),
    }),

  // Plaid
  createLinkToken: () => request('/api/plaid/link-token', { method: 'POST' }),

  exchangeToken: (publicToken, institutionName, institutionId) =>
    request('/api/plaid/exchange', {
      method: 'POST',
      body: JSON.stringify({ publicToken, institutionName, institutionId }),
    }),

  getAccounts: () => request('/api/plaid/accounts'),

  syncAccounts: () => request('/api/plaid/sync', { method: 'POST' }),

  disconnectAccount: (itemId, mode = 'soft') =>
    request(`/api/plaid/items/${itemId}?mode=${mode}`, { method: 'DELETE' }),

  // Transactions
  getSummary: () => request('/api/transactions/summary'),

  getTransactions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/transactions?${qs}`);
  },

  getSpendingByMonth: () => request('/api/transactions/spending-by-month'),
  getInvestmentWithdrawals: () => request('/api/investments/withdrawals'),
  getTransferCandidates: () => request('/api/investments/transfer-candidates'),
  reviewTransferCandidate: (transactionId, action) =>
    request(`/api/investments/transfer-candidates/${transactionId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  getTopByMonth: (months, limit) => request(`/api/transactions/top-by-month?months=${months}&limit=${limit}`),

  getBigPurchases: (year) => request(`/api/transactions/big-purchases?year=${year}`),

  updateBigPurchaseThreshold: (threshold) =>
    request('/api/auth/big-purchase-threshold', {
      method: 'PUT',
      body: JSON.stringify({ threshold }),
    }),

  updateBigPurchaseExcludedCategories: (categories) =>
    request('/api/auth/big-purchase-excluded-categories', {
      method: 'PUT',
      body: JSON.stringify({ categories }),
    }),

  getFixedCostCategories: () => request('/api/auth/fixed-cost-categories'),
  updateFixedCostCategories: (categories) =>
    request('/api/auth/fixed-cost-categories', {
      method: 'PUT',
      body: JSON.stringify({ categories }),
    }),

  updateExcludeBigPurchasesFromBudget: (enabled) =>
    request('/api/auth/exclude-big-purchases-from-budget', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  getTransactionsList: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return request(`/api/transactions${qs ? `?${qs}` : ''}`);
  },

  getMonthlyRecap: (params) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return request(`/api/transactions/recap${qs ? `?${qs}` : ''}`);
  },

  getIncomeTransactions: (params) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return request(`/api/income/transactions${qs ? `?${qs}` : ''}`);
  },

  getTransferTransactions: (params) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return request(`/api/income/transfers${qs ? `?${qs}` : ''}`);
  },

  markNotTransfer: (merchantName) =>
    request('/api/income/transfers/not-transfer', {
      method: 'POST',
      body: JSON.stringify({ merchantName }),
    }),

  getNotTransferList: () => request('/api/income/transfers/not-transfer-list'),

  reverseNotTransfer: (merchantName) =>
    request(`/api/income/transfers/not-transfer/${encodeURIComponent(merchantName)}`, { method: 'DELETE' }),

  getRecapMonths: (params) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return request(`/api/transactions/recap-months${qs ? `?${qs}` : ''}`);
  },

  getTopSnapshot: () => request('/api/transactions/top-snapshot'),

  // Subscriptions
  getSubscriptions: () => request('/api/subscriptions'),

  syncSubscriptions: () =>
    request('/api/subscriptions/sync', { method: 'POST' }),

  dismissSubscription: (id) =>
    request(`/api/subscriptions/${id}`, { method: 'DELETE' }),

  setSubscriptionNickname: (id, nickname) =>
    request(`/api/subscriptions/${id}/nickname`, {
      method: 'PUT',
      body: JSON.stringify({ nickname }),
    }),

  ignoreSubscription: (id) =>
    request(`/api/subscriptions/${id}/ignore`, { method: 'PUT' }),

  unignoreSubscription: (id) =>
    request(`/api/subscriptions/${id}/unignore`, { method: 'PUT' }),

  dismissChargeWarning: (id) =>
    request(`/api/subscriptions/${id}/dismiss-charge-warning`, { method: 'PUT' }),

  dismissAllChargeWarnings: () =>
    request('/api/subscriptions/dismiss-all-charge-warnings', { method: 'PUT' }),

  getIgnoredSubscriptions: () => request('/api/subscriptions/ignored'),

  // MAGI
  getMagi: () => request('/api/magi'),

  addIncome: (data) =>
    request('/api/magi/income', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteIncome: (id) =>
    request(`/api/magi/income/${id}`, { method: 'DELETE' }),

  matchIncomeEntry: (id, transactionId) =>
    request(`/api/magi/income/${id}/match`, {
      method: 'POST',
      body: JSON.stringify({ transactionId }),
    }),

  dismissIncomeMatch: (id, transactionId) =>
    request(`/api/magi/income/${id}/dismiss-match`, {
      method: 'POST',
      body: JSON.stringify({ transactionId }),
    }),

  // Planned Expenses
  getPlannedExpenses: () => request('/api/planned-expenses'),

  addPlannedExpense: (data) =>
    request('/api/planned-expenses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  togglePlannedExpensePaid: (id) =>
    request(`/api/planned-expenses/${id}/toggle-paid`, { method: 'PUT' }),

  confirmPlannedExpenseMatch: (id, transactionId) =>
    request(`/api/planned-expenses/${id}/confirm-match`, {
      method: 'PUT',
      body: JSON.stringify({ transactionId }),
    }),

  dismissPlannedExpenseMatch: (id, transactionId) =>
    request(`/api/planned-expenses/${id}/dismiss-match`, {
      method: 'PUT',
      body: JSON.stringify({ transactionId }),
    }),

  deletePlannedExpense: (id) =>
    request(`/api/planned-expenses/${id}`, { method: 'DELETE' }),

  // Investments / Net Worth
  getInvestments: () => request('/api/investments'),
  getInvestmentWithdrawals: () => request('/api/investments/withdrawals'),

  getInvestmentHistory: () => request('/api/investments/history'),

  addRealEstate: (data) =>
    request('/api/investments/real-estate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRealEstate: (id, data) =>
    request(`/api/investments/real-estate/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteRealEstate: (id) =>
    request(`/api/investments/real-estate/${id}`, { method: 'DELETE' }),

  updateWithdrawalRate: (rate) =>
    request('/api/investments/withdrawal-rate', {
      method: 'PUT',
      body: JSON.stringify({ rate }),
    }),

  // Analytics
  trackEvent: (eventType, tabId) =>
    request('/api/analytics/track', {
      method: 'POST',
      body: JSON.stringify({ eventType, tabId }),
    }),

  getAdminStats: () => request('/api/analytics/stats'),

  // Tickers
  getTickerStatus: () => request('/api/tickers/status'),

  saveFinnhubKey: (apiKey) =>
    request('/api/tickers/finnhub-key', {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    }),

  deleteFinnhubKey: () => request('/api/tickers/finnhub-key', { method: 'DELETE' }),

  getTickers: () => request('/api/tickers'),

  addTicker: (ticker) =>
    request('/api/tickers', {
      method: 'POST',
      body: JSON.stringify({ ticker }),
    }),

  removeTicker: (ticker) =>
    request(`/api/tickers/${ticker}`, { method: 'DELETE' }),

  toggleTickerOnOverview: (ticker, show) =>
    request(`/api/tickers/${ticker}/show-on-overview`, {
      method: 'PUT',
      body: JSON.stringify({ show }),
    }),

  // AI Chat
  getAIStatus: () => request('/api/ai/status'),

  saveAIKey: (provider, apiKey) =>
    request('/api/ai/keys', {
      method: 'PUT',
      body: JSON.stringify({ provider, apiKey }),
    }),

  deleteAIKey: (provider) =>
    request(`/api/ai/keys/${provider}`, { method: 'DELETE' }),

  setPreferredAIProvider: (provider) =>
    request('/api/ai/preferred-provider', {
      method: 'PUT',
      body: JSON.stringify({ provider }),
    }),

  sendAIMessage: (provider, message, history) =>
    request('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ provider, message, history }),
    }),

  getAIRecap: (period, provider) =>
    request(`/api/ai/recap?period=${period}&provider=${provider}`),

  // Spending breakdown (category + fixed vs one-off pie data)
  getSpendingBreakdown: (period) =>
    request(`/api/transactions/spending-breakdown?period=${period}`),

  // Household members
  getHouseholdMembers: () => request('/api/household/members'),

  updateMemberAnnualIncome: (userId, annualIncome) =>
    request(`/api/household/members/${userId}/annual-income`, {
      method: 'PUT',
      body: JSON.stringify({ annualIncome }),
    }),

  updateMemberVisibleTabs: (userId, tabs) =>
    request(`/api/household/members/${userId}/visible-tabs`, {
      method: 'PUT',
      body: JSON.stringify({ tabs }),
    }),

  // Sponsors
  getSponsors: (placement) => request(`/api/sponsors?placement=${placement}`),

  getAllSponsorsAdmin: () => request('/api/sponsors/admin/all'),

  createSponsor: (data) =>
    request('/api/sponsors/admin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSponsor: (id, data) =>
    request(`/api/sponsors/admin/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteSponsor: (id) =>
    request(`/api/sponsors/admin/${id}`, { method: 'DELETE' }),

  getSponsorSlotLimits: () => request('/api/sponsors/admin/slot-limits'),

  updateSponsorSlotLimit: (placement, maxSlots) =>
    request(`/api/sponsors/admin/slot-limits/${placement}`, {
      method: 'PUT',
      body: JSON.stringify({ maxSlots }),
    }),

  updateInvestmentGoal: (amount) =>
    request('/api/investments/monthly-goal', {
      method: 'PUT',
      body: JSON.stringify({ amount }),
    }),

  updateInvestmentGoalPercent: (pct) =>
    request('/api/investments/goal-percent', {
      method: 'PUT',
      body: JSON.stringify({ pct }),
    }),

  // Manual Transactions
  addManualTransaction: (data) =>
    request('/api/transactions/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteTransaction: (id) =>
    request(`/api/transactions/${id}`, { method: 'DELETE' }),

  // Real Estate
  getRealEstate: () => request('/api/real-estate'),
  addProperty: (data) =>
    request('/api/real-estate', { method: 'POST', body: JSON.stringify(data) }),
  updateProperty: (id, data) =>
    request(`/api/real-estate/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProperty: (id) =>
    request(`/api/real-estate/${id}`, { method: 'DELETE' }),
  getPropertyFeedback: (id, provider) =>
    request(`/api/real-estate/${id}/feedback?provider=${provider}`),

  // Health
  health: () => request('/health'),
};

export const fmt = {
  currency: (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n || 0),

  pct: (n) => `${Math.round(n || 0)}%`,

  date: (d) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),

  shortDate: (d) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
};
